import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useHelpContext } from "../help/HelpContext";
import { useAccess } from "../access/AccessContext";
import { PERMISSIONS } from "../access/capabilities";

import IncidentTypeManager from "./IncidentTypeManager";
import AssetTypeManager from "./AssetTypeManager";
import AssetManager from "./AssetManager";
import RiskOwnerManager from "./RiskOwnerManager";
import TreatmentOptionManager from "./TreatmentOptionManager";
import TreatmentApplicabilityManager from "./TreatmentApplicabilityManager";
import TreatmentCostManager from "./TreatmentCostManager";
import TreatmentEffectManager from "./TreatmentEffectManager";

const tabGroups = [{
  label: "Scenario Context",
  tabs: [
  {
    id: "incident-types",
    label: "Incident Types",
    component: IncidentTypeManager,
    permissions: [PERMISSIONS.risk.change],
  },
  {
    id: "business-processes",
    label: "Business Processes",
    component: AssetTypeManager,
    permissions: [PERMISSIONS.risk.change],
  },
  {
    id: "assets",
    label: "Assets",
    component: AssetManager,
    permissions: [PERMISSIONS.risk.change],
  },
  {
    id: "risk-owners",
    label: "Risk Owners",
    component: RiskOwnerManager,
    permissions: [PERMISSIONS.risk.change],
  },
  ],
}, {
  label: "Treatment Data",
  tabs: [
  {
    id: "treatment-options",
    label: "Treatment Options",
    component: TreatmentOptionManager,
    permissions: [PERMISSIONS.risk.treatmentOptionAdd, PERMISSIONS.risk.treatmentOptionChange],
  },
  {
    id: "applicability",
    label: "Applicability",
    component: TreatmentApplicabilityManager,
    permissions: [PERMISSIONS.risk.applicabilityAdd, PERMISSIONS.risk.applicabilityChange],
  },
  {
    id: "treatment-costs",
    label: "Costs",
    component: TreatmentCostManager,
    permissions: [PERMISSIONS.risk.treatmentCostAdd, PERMISSIONS.risk.treatmentCostChange],
  },
  {
    id: "treatment-effects",
    label: "Effects",
    component: TreatmentEffectManager,
    permissions: [PERMISSIONS.risk.treatmentEffectAdd, PERMISSIONS.risk.treatmentEffectChange],
  },
  ],
}];
const tabs = tabGroups.flatMap((group) => group.tabs);

export default function RiskDataPage() {
  const access = useAccess();
  const navigate = useNavigate();
  const { section } = useParams();

  const activeTab = section || "incident-types";
  const { setCurrentViewId } = useHelpContext();

  useEffect(() => {
    setCurrentViewId(activeTab);
    return () => setCurrentViewId(null);
  }, [activeTab, setCurrentViewId]);

  const ActiveComponent =
    tabs.find((tab) => tab.id === activeTab)?.component ||
    IncidentTypeManager;
  const activeDefinition = tabs.find((tab) => tab.id === activeTab) || tabs[0];
  const canMaintain = access.canAny(activeDefinition.permissions);

  return (
    <div className="p-6 space-y-6 bg-slate-50 min-h-screen">
 
      <div >
        <p className="text-xs uppercase tracking-[0.25em] text-slate-400">
            Scenario Modeling
        </p>

        <h1 className="text-2xl font-semibold text-slate-900 mt-2">
          Scenario Data
        </h1>

        <p className="text-slate-500 mt-2 max-w-3xl mt-1">
           Manage reference data used by risk scenarios, assets, ownership and
           business impact analysis.
        </p>

      </div>

      <div className="bg-white border rounded-2xl shadow-sm">
        <div className="flex flex-wrap gap-x-6 gap-y-3 border-b p-3">
          {tabGroups.map((group) => <div key={group.label} aria-label={group.label} className="space-y-1">
            <p className="px-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">{group.label}</p>
            <div className="flex flex-wrap gap-2">
              {group.tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  aria-current={activeTab === tab.id ? "page" : undefined}
                  onClick={() => navigate(`/risk-data/${tab.id}`)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
                    activeTab === tab.id
                      ? "bg-slate-900 text-white"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>)}
        </div>

        <div className="p-4">
          {canMaintain ? <ActiveComponent /> : (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-6" data-testid="risk-data-read-only">
              <h2 className="font-semibold text-slate-900">Read-only scenario data</h2>
              <p className="mt-2 text-sm text-slate-600">This governed reference-data area is available for context. Maintenance controls require the corresponding Risk Data capability.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
